import Form from '.';
import { arrayify } from "../../../../lib/utils/array";
import { DEFAULT_EDITORS_FIT_DATA_ORDER } from "../../../../lib/utils/consts";
import getFormItemLayout from '../../plugins/form/getFormItemLayout';
import { DataStub, defineLazyGetter } from '@tencent/data-schema-core';
import { flattenDataSchema } from "../../../../lib/utils/dataSchema";
import { getFitDataLayout, walkLayout, getLayoutSlot } from "../../../../lib/utils";
import { defaults, get, cloneDeep } from "lodash-es";
import { vueDeepSet } from "../../../../lib/utils/vue";
import { vueDeepDelete } from "../../../../lib/utils/vue/vueDeepAssign";
import { joinPath } from '@tencent/data-schema-core/lib/utils/path';

/** @type {UICore.ComponentMeta} */
const FormComponentMeta = {
  id: 'w-form',
  name: '表单',
  icon: 'https://vfiles.gtimg.cn/wuji_dashboard/xy/rainbow/icon-form_1636364104837.png',
  category: '表单',
  component: Form,
  slots: [{
    id: 'default',
    containerRef: 'container'
  }],
  stylePanel: {
    selectors: [{
      selector: '.w-formItem',
      name: '表单项'
    }, {
      selector: '.w-formItem-editor',
      name: '表单项-输入区域'
    }, {
      selector: '.w-formItem-label',
      name: '表单项-标签区域'
    }]
  },
  propsSchema: {
    type: 'object',
    fields: [{
      id: 'readonly',
      type: 'boolean',
      title: '只读',
      ui: {
        type: 'multiRatioInput'
      }
    }, {
      id: 'required',
      type: 'boolean',
      title: '必填',
      ui: {
        type: 'multiRatioInput'
      }
    }, {
      id: 'hasSuccessFeedback',
      type: 'boolean',
      title: '校验成功反馈',
      ui: {
        type: 'multiRatioInput'
      }
    }, {
      id: 'disabled',
      type: 'boolean',
      title: '禁用',
      ui: {
        type: 'multiRatioInput'
      }
    }, {
      id: 'fillLine',
      type: 'boolean',
      title: '占满整行'
    }, {
      id: 'labelWidth',
      type: 'string',
      title: '标签宽度',
      description: '50px , 100% , 20vw 等等',
      ui: {
        settings: {
          placeholder: '留空则用默认值，示例：100px'
        }
      }
    }, {
      id: 'layout',
      type: 'string',
      title: '标签水平对齐形式',
      enum: [{
        label: '自动决定',
        value: ''
      }, {
        label: '右对齐',
        value: 'horizontal'
      }, {
        label: '顶对齐',
        value: 'vertical'
      }, {
        label: '左对齐',
        value: 'horizontal_left'
      }]
    }, {
      id: 'labelVertical',
      type: 'string',
      title: '标签垂直对齐形式',
      enum: [{
        label: '继承上级',
        value: ''
      }, {
        label: '默认',
        value: 'default'
      }, {
        label: '向上对齐',
        value: 'start'
      }, {
        label: '居中',
        value: 'center'
      }, {
        label: '向下对齐',
        value: 'end'
      }]
    }, {
      id: 'titleMode',
      title: '描述&提示渲染方式',
      type: 'string',
      enum: [{
        label: '自动决定',
        value: ''
      }, {
        value: 'default',
        label: '单行文本'
      }, {
        value: 'pre',
        label: '多行文本(PRE)'
      }, {
        value: 'markdown',
        label: 'Markdown'
      }]
    }, {
      id: 'mobileMode',
      title: '移动端渲染',
      type: 'string',
      enum: [{
        label: '自动优化布局',
        value: ''
      }, {
        value: 'manual',
        label: '遵循用户设置'
      }]
    }, {
      id: 'autoValidate',
      type: 'string',
      title: '自动校验',
      enum: [{
        label: '不需要',
        value: 'none'
      }, {
        label: '失去焦点自动校验',
        value: 'blur'
      }
      // {label:'inline'	,value:'inline'	},
      ]
    }, {
      id: 'className',
      type: 'string',
      hidden: true
    }, {
      id: 'css',
      title: '自定义CSS',
      type: 'string',
      ui: {
        settings: {
          multipleLine: true
        }
      },
      hidden: true
    }]
  },
  // getLayoutName(layout) {
  //   const { componentId } = layout;
  //   const notNamed = !componentId || componentId.startsWith(AUTO_COMPONENT_ID_PREFIX);
  //   if (notNamed) return '未命名表单';
  //   return `${componentId} 表单`;
  // },
  getActions({
    layout,
    replaceCompWith,
    designer,
    uc,
    renderer,
    modifyComp
  }) {
    return [{
      title: '打散',
      icon: 'fa fa-scissors',
      desc: '拆除表单容器，但是保留里面的内容',
      execute: () => replaceCompWith(...(layout.children || []))
    }, {
      place: ['propPanel'],
      title: '重置表单项',
      icon: 'fa fa-undo',
      desc: '将全部子表单的只读、必填、校验、标签宽度、标签对齐配置设为"继承上级表单"',
      execute: async () => {
        await modifyComp(comp => {
          walkLayout(comp, step => {
            if (step.layout.type !== 'w-form-item') return;
            const RESET_FIELDS = ['labelWidth', 'layout', 'hasSuccessFeedback', 'required', 'readonly'];
            RESET_FIELDS.forEach(field => {
              vueDeepDelete(step.layout, `props.${field}`);
            });
          });
        });
      }
    }, {
      place: ['propPanel'],
      title: '对齐表单项宽度',
      icon: 'fa fa-text-width',
      desc: '按照第一个表单项对齐表单项中的组件宽度',
      execute: async () => {
        await modifyComp(comp => {
          let firstWidth = undefined;
          const getFirstFormItemChildrenFromStep = step => {
            if (step.layout.type !== 'w-form-item') return;
            const childrens = getLayoutSlot(step.layout, 'default', false) || [];
            const children = childrens[0];
            return children;
          };
          // 找第一个formItem里的组件 查询宽度设置
          walkLayout(comp, step => {
            const children = getFirstFormItemChildrenFromStep(step);
            if (!children) return;
            const width = get(children, 'style.width');
            firstWidth = width;
            return 'abort';
          });
          // firstWidth可能是undefined 这样可以恢复默认宽度
          walkLayout(comp, step => {
            const children = getFirstFormItemChildrenFromStep(step);
            if (!children) return;
            if (!firstWidth) {
              vueDeepDelete(children, 'style.width');
            } else {
              vueDeepSet(children, 'style.width', firstWidth);
            }
          });
        });
      }
    }, {
      place: ['propPanel'],
      title: '添加表单项',
      icon: 'fa fa-plus-square-o',
      execute: async e => {
        const dataPath = await designer.openDataPathSelector({
          place: e.target
        });
        if (!dataPath) return;
        const res = uc.fitData(dataPath, renderer, {
          compTypeFilter: 'all'
        }) || [];
        const fitDataResult = res.find(item => {
          var _getFitDataLayout$lay;
          return item.type === 'editor' && ((_getFitDataLayout$lay = getFitDataLayout(item).layout) === null || _getFitDataLayout$lay === void 0 ? void 0 : _getFitDataLayout$lay.type) === 'w-form-item';
        });
        const {
          layout
        } = getFitDataLayout(cloneDeep(fitDataResult));
        if (!layout) return;
        await modifyComp(comp => {
          uc.completeLayout(layout);
          defaults(comp, {
            children: []
          });
          comp.children.push(layout);
        });
      }
    }];
  },
  fitData({
    dataStub,
    renderer,
    uc,
    options
  }) {
    if (dataStub.type !== 'object' || !dataStub.definedFields.length) return null;
    /** @type {UICore.Layout} */
    const layout = {
      type: 'w-form',
      children: [{
        type: 'w-card',
        children: [],
        // 这是个 getter 哦
        props: {
          title: options.cardTitle ? dataStub.getTitle() || '' : ''
        }
      }]
    };
    const layoutGetter = () => {
      /** @type {UICore.Layout[]} */
      const children = [];

      // ------ 表单标题 -------

      // const title = dataStub.getTitle();
      // if (title) {
      //   children.push({
      //     type: 'w-text',
      //     props: {
      //       text: title,
      //       type: 'heading',
      //     },
      //   });
      // }

      // ------- 表单内容 ---------
      const getFields = () => {
        if (options.flattenDataSchema === false) {
          var _stub$definedFields;
          const stub = new DataStub({
            schema: dataStub.schema
          });
          const fields = (_stub$definedFields = stub.definedFields) === null || _stub$definedFields === void 0 ? void 0 : _stub$definedFields.map(subSchema => {
            const subStub = stub.getChildStub(subSchema.id);
            if (!subStub) return undefined;
            const subPath = joinPath([...subStub.pathParts]);
            return {
              path: subPath
            };
          }).filter(Boolean);
          return fields;
        }
        return flattenDataSchema(dataStub.schema);
      };
      const fields = getFields();
      fields.forEach(({
        path
      }) => {
        const subStub = dataStub.getStubAtPath(path);
        if (!subStub) return;
        if (options.flattenDataSchema !== false && subStub.type === 'object') return;
        if (subStub.schema.hidden) return;
        const editor = uc.fitData(subStub, renderer, options).find(x => x.type === 'editor' && x.layout.type !== 'w-form-item');
        if (!editor) return;
        const {
          layout
        } = getFitDataLayout(editor);
        if (!(layout !== null && layout !== void 0 && layout.type)) return;
        children.push(getFormItemLayout(subStub, arrayify(layout), editor));
      });
      return children;
    };
    if (options.cardInForm === false) {
      layout.children = [];
      defineLazyGetter(layout, 'children', layoutGetter);
    } else {
      defineLazyGetter(layout.children[0], 'children', layoutGetter);
    }
    // -----------------------------

    return {
      type: 'editor',
      order: DEFAULT_EDITORS_FIT_DATA_ORDER,
      layout
    };
  },
  placeCompInside(ctx) {
    var _ctx$layout$type;
    // 仅允许表单项装入
    if ((_ctx$layout$type = ctx.layout.type) !== null && _ctx$layout$type !== void 0 && _ctx$layout$type.includes('w-form-item')) return 'default';
    return undefined;
  }
};
export default FormComponentMeta;
//# sourceMappingURL=meta.js.map
